import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import DataTablePagination from '../../Generic/DataTablePagination';
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useStore } from '../../../stores/store';
import { useSetMenu } from "../../../hooks/useSetMenu";

const StudentAttendanceList = () => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const localizedTextsMap = useLocalizedTextsMap();
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [attendanceStatuses, setAttendanceStatuses] = useState([]);
  const [uniqueGroups, setUniqueGroups] = useState([]);
  const [hasEndSession, setHasEndSession] = useState(false);
  const { mainStore } = useStore();
  useSetMenu(mainStore.SchedulePlanMenuType, mainStore.SchedulePlanMenuNo);

  // Pagination state for DataGrid
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const schedulePlanWeekId = query.get('schedulePlanWeekId');
  const navigate = useNavigate();

  // Helper function to get localized status name based on current language
  // Language mapping: lng1 = NameLNG1 (Albanian), lng2 = NameLNG2 (English), lng3 = NameLNG3 (Turkish)
  const getLocalizedStatusName = useCallback((status) => {
    const currentLang = i18n.language?.toLowerCase() || 'lng1';

    if (currentLang === 'lng2' || currentLang === 'en') {
      return status.nameLNG2 || status.nameLng2 || status.nameLNG1 || status.nameLng1 || status.name || `Status ${status.id}`;
    } else if (currentLang === 'lng3' || currentLang === 'tr') {
      return status.nameLNG3 || status.nameLng3 || status.nameLNG1 || status.nameLng1 || status.name || `Status ${status.id}`;
    } else {
      // Default to lng1 (Albanian) or fallback
      return status.nameLNG1 || status.nameLng1 || status.name || `Status ${status.id}`;
    }
  }, []);

  // Function to fetch attendance statuses
  useEffect(() => {
    axiosPrivate
      .get('/AttendanceStatus')
      .then(response => {
        // Remove any duplicate statuses by ID and filter out status with ID 7 (double)
        const uniqueStatuses = response.data.reduce((acc, current) => {
          // Skip status ID 7 (double)
          if (current.id === 7) return acc;

          const isDuplicate = acc.find(item => item.id === current.id);
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        }, []);
        setAttendanceStatuses(uniqueStatuses || []);
      })
      .catch(error => {
        console.error("Error fetching attendance statuses:", error);
      });
  }, [axiosPrivate]);

  // Fetch attendance data
  useEffect(() => {
    if (schedulePlanWeekId) {
      setLoading(true);
      axiosPrivate
        .get(`/SchedulePlanWeekSessions/GetAttendanceBySchedulePlanWeek/${schedulePlanWeekId}`)
        .then(response => {
          // Filter out records with status ID 7 (double)
          const filteredRecords = response.data.filter(record => record.attendanceStatusId !== 7);
          setAttendanceData(filteredRecords);

          // Extract unique groups
          const groups = [...new Set(filteredRecords.map(r => r.groupName).filter(Boolean))].sort();
          setUniqueGroups(groups);

          setLoading(false);
        })
        .catch(error => {
          console.error("Error fetching attendance data:", error);
          enqueueSnackbar(t("errorFetchingAttendanceData"), { variant: "error" });
          setLoading(false);
        });

      // Fetch attendance statistics
      axiosPrivate
        .get(`/SchedulePlanWeekSessions/GetAttendanceStatistics/${schedulePlanWeekId}`)
        .then(response => {
          setStatistics(response.data);
          setHasEndSession(response.data.hasEndSession || false);
        })
        .catch(error => {
          console.error("Error fetching attendance statistics:", error);
          // Create default statistics to avoid UI issues
          setStatistics({
            totalEnrolled: 0,
            present: 0,
            attendanceRate: 0,
            fullAttendance: 0,
            leftEarly: 0,
            late: 0,
            hasEndSession: false
          });
        });
    }
  }, [schedulePlanWeekId, axiosPrivate, enqueueSnackbar, t]);

  // Function to update attendance status using StudentId and SchedulePlanWeekId
  const updateAttendanceStatus = async (studentId, newStatusId) => {
    try {
      await axiosPrivate.put(`/SchedulePlanWeekSessions/UpdateStudentAttendanceStatus`, {
        studentId: studentId,
        schedulePlanWeekId: parseInt(schedulePlanWeekId),
        statusId: newStatusId
      });

      // Update local state
      setAttendanceData(prevData =>
        prevData.map(item =>
          item.studentId === studentId
            ? { ...item, attendanceStatusId: newStatusId }
            : item
        )
      );

      enqueueSnackbar(t("attendanceStatusUpdated"), { variant: "success" });

      // Refresh statistics
      axiosPrivate
        .get(`/SchedulePlanWeekSessions/GetAttendanceStatistics/${schedulePlanWeekId}`)
        .then(response => setStatistics(response.data));
    } catch (error) {
      console.error("Error updating attendance status:", error);
      enqueueSnackbar(t("errorUpdatingAttendanceStatus"), { variant: "error" });
    }
  };

  // Filter attendance data based on status and group
  let filteredData = attendanceData;

  if (statusFilter !== 'all') {
    filteredData = filteredData.filter(record => record.attendanceStatusId === parseInt(statusFilter));
  }

  if (groupFilter !== 'all') {
    filteredData = filteredData.filter(record => record.groupName === groupFilter);
  }

  // Calculate group statistics
  const groupStats = {};
  attendanceData.forEach(record => {
    const group = record.groupName || 'No Group';
    if (!groupStats[group]) {
      groupStats[group] = { total: 0, present: 0 };
    }
    groupStats[group].total++;
    if (record.attendanceStatusId === 1) {
      groupStats[group].present++;
    }
  });

  // Helper function to format date/time
  const formatDateTime = (dateValue) => {
    if (!dateValue) return null;
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return dateValue;

      const formattedDate = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const formattedTime = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });

      return { formattedDate, formattedTime };
    } catch (e) {
      return null;
    }
  };

  // Helper function to get status display info
  const getStatusInfo = (statusId) => {
    const status = attendanceStatuses.find(s => s.id === statusId);
    let statusName = "Unknown";
    if (status) {
      statusName = getLocalizedStatusName(status);
    } else if (statusId === 1) {
      statusName = t("present");
    } else if (statusId === 2) {
      statusName = t("absent");
    } else if (statusId === 3) {
      statusName = t("excused");
    } else if (statusId === 4) {
      statusName = t("late");
    } else if (statusId === 5) {
      statusName = t("leftEarly");
    } else if (statusId === 6) {
      statusName = t("fullAttendance");
    }

    const statusConfig = {
      1: { color: 'success', icon: 'check-circle-fill' },
      2: { color: 'danger', icon: 'x-circle-fill' },
      3: { color: 'warning', icon: 'exclamation-circle-fill' },
      4: { color: 'info', icon: 'clock-fill' },
      5: { color: 'warning', icon: 'box-arrow-right' },
      6: { color: 'success', icon: 'check-all' }
    };

    const config = statusConfig[statusId] || { color: 'secondary', icon: 'question-circle-fill' };
    return { statusName, config };
  };

  // Define columns for the DataGrid.
  const columns = [
    {
      field: 'idstudentit',
      headerName: t("studentId"),
      flex: 0.7,
      minWidth: 90,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
    },
    {
      field: 'studentName',
      headerName: t("studentName"),
      flex: 1.2,
      minWidth: 150,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderCell: (params) => (
        <div className="d-flex align-items-center">
          <i className="bi bi-person-circle me-2 text-primary"></i>
          <span className="fw-medium">{params.value}</span>
        </div>
      )
    },
    {
      field: 'groupName',
      headerName: t("group"),
      flex: 0.8,
      minWidth: 100,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderCell: (params) => {
        const groupName = params.value;
        const isGroupMatch = params.row.isGroupMatch;

        if (!groupName) {
          // No group assigned for this semester plan
          return (
            <span className="badge bg-light text-muted border px-2 py-1">
              <i className="bi bi-dash-circle me-1"></i>
              N/A
            </span>
          );
        }

        if (isGroupMatch) {
          // Student belongs to this lecture's group
          return (
            <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary px-2 py-1">
              <i className="bi bi-people-fill me-1"></i>
              {groupName}
            </span>
          );
        }

        // Student has a group but is attending a different group's lecture
        return (
          <span className="badge bg-danger bg-opacity-10 text-danger border border-danger px-2 py-1" title={t("studentFromDifferentGroup")}>
            <i className="bi bi-arrow-left-right me-1"></i>
            {groupName}
          </span>
        );
      }
    },
    {
      field: 'startAttendanceDate',
      headerName: t("startScan"),
      flex: 1,
      minWidth: 130,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderCell: (params) => {
        const formatted = formatDateTime(params.value);
        if (!formatted) {
          return (
            <span className="badge bg-light text-muted border px-2 py-1">
              <i className="bi bi-dash-circle me-1"></i>
              {t("notScanned")}
            </span>
          );
        }

        return (
          <div className="d-flex flex-column">
            <span className="text-success fw-medium small">
              <i className="bi bi-check-circle-fill me-1"></i>
              {formatted.formattedTime}
            </span>
            <span className="text-muted" style={{ fontSize: '0.7rem' }}>
              {formatted.formattedDate}
            </span>
          </div>
        );
      }
    },
    {
      field: 'endAttendanceDate',
      headerName: t("endScan"),
      flex: 1,
      minWidth: 130,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderCell: (params) => {
        const formatted = formatDateTime(params.value);
        if (!formatted) {
          return (
            <span className="badge bg-light text-muted border px-2 py-1">
              <i className="bi bi-dash-circle me-1"></i>
              {hasEndSession ? t("notScanned") : t("noEndSession")}
            </span>
          );
        }

        return (
          <div className="d-flex flex-column">
            <span className="text-success fw-medium small">
              <i className="bi bi-check-circle-fill me-1"></i>
              {formatted.formattedTime}
            </span>
            <span className="text-muted" style={{ fontSize: '0.7rem' }}>
              {formatted.formattedDate}
            </span>
          </div>
        );
      }
    },
    {
      field: 'calculatedStatusId',
      headerName: t("finalStatus"),
      flex: 1.2,
      minWidth: 140,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderCell: (params) => {
        // Use calculatedStatusId if available, otherwise fall back to attendanceStatusId
        const statusId = params.value || params.row.attendanceStatusId;
        const { statusName, config } = getStatusInfo(statusId);

        return (
          <span className={`badge bg-${config.color} px-2 py-1`}>
            <i className={`bi bi-${config.icon} me-1`}></i>
            {statusName}
          </span>
        );
      }
    },
    {
      field: 'attendanceStatusId',
      headerName: t("actions"),
      flex: 1,
      minWidth: 140,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderCell: (params) => {
        const statusId = params.value;

        return (
          <select
            className="form-select form-select-sm"
            value={statusId}
            onChange={(e) => updateAttendanceStatus(params.row.studentId, parseInt(e.target.value))}
            style={{ maxWidth: '130px', fontSize: '0.8rem' }}
          >
            {attendanceStatuses.map(status => (
              <option key={status.id} value={status.id}>
                {getLocalizedStatusName(status)}
              </option>
            ))}
          </select>
        );
      }
    }
  ];

  // Format the replacement-replaces date string once for both loading and main render.
  const formatShortDate = (d) => {
    if (!d) return null;
    try {
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return null; }
  };

  if (loading) {
    return (
      <div className="container-fluid py-3">
        <div className="card mb-4">
          <div className="card-header bg-brand text-white d-flex justify-content-between align-items-center py-3">
            <div>
              <h4 className="mb-0">{t("studentAttendanceList")}</h4>
              <small className="opacity-75">{t("viewAttendance")}</small>
            </div>
            <button
              className="btn btn-light"
              onClick={() => navigate(-1)}
              title={t("backToList")}
            >
              <i className="bi bi-arrow-left me-1"></i> {t("backToList")}
            </button>
          </div>
        </div>
        <div className="text-center py-5">
          <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
          <p className="mt-3 text-muted">{t("loadingAttendanceData")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      {/* Red Header with Back Button - matching QR Code page style */}
      <div className="card mb-4">
        <div className="card-header bg-brand text-white d-flex justify-content-between align-items-center py-3">
          <div>
            <h4 className="mb-0">
              {t("studentAttendanceList")}
              {statistics?.subjectName && <span className="ms-2"> — {statistics.subjectName}</span>}
            </h4>
            <small className="opacity-75 d-flex align-items-center flex-wrap gap-2 mt-1">
              <span>{t("viewAttendance")}</span>
              {statistics?.weekDay && <span>· {formatShortDate(statistics.weekDay)}</span>}
              {statistics?.replacedSchedulePlanWeekId && statistics?.replacedWeekDay && (
                <span
                  className="badge bg-light text-dark"
                  style={{ fontSize: '0.7rem' }}
                  title={statistics.replacementReasonName || ''}
                >
                  ↺ {t("replaces") || "Replaces"} {formatShortDate(statistics.replacedWeekDay)}
                </span>
              )}
              {statistics?.isReplaced && (
                <span className="badge bg-dark" style={{ fontSize: '0.7rem' }}>
                  {t("statusReplaced") || "Replaced"}
                </span>
              )}
            </small>
          </div>
          <button
            className="btn btn-light"
            onClick={() => navigate(-1)}
            title={t("backToList")}
          >
            <i className="bi bi-arrow-left me-1"></i> {t("backToList")}
          </button>
        </div>
      </div>

      {/* Replacement info banner — shown when this lecture is a replacement for an earlier week. */}
      {statistics?.replacedSchedulePlanWeekId && statistics?.replacedWeekDay && (
        <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
          <i className="bi bi-arrow-repeat me-2" style={{ fontSize: '1.2rem' }}></i>
          <div>
            <strong>{t("replacementLecture") || "Replacement lecture"}.</strong>{' '}
            {t("replaces") || "Replaces"} <strong>{formatShortDate(statistics.replacedWeekDay)}</strong>
            {statistics.replacementReasonName && (
              <>
                {' '}— {t("replacementReason") || "Reason"}:{' '}
                <strong>{statistics.replacementReasonName}</strong>
              </>
            )}
          </div>
        </div>
      )}
      {/* Enhanced Statistics Cards */}
      {statistics && (
        <>
          {/* Main Statistics Row */}
          <div className="row mb-3 g-3">
            <div className="col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-primary mb-2">
                    <i className="bi bi-people-fill fs-1"></i>
                  </div>
                  <h3 className="fw-bold text-dark mb-1">{statistics.totalEnrolled}</h3>
                  <p className="text-muted mb-0 small">{t("totalEnrolled")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-success mb-2">
                    <i className="bi bi-check-circle-fill fs-1"></i>
                  </div>
                  <h3 className="fw-bold text-dark mb-1">{statistics.present}</h3>
                  <p className="text-muted mb-0 small">{t("present")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-danger mb-2">
                    <i className="bi bi-x-circle-fill fs-1"></i>
                  </div>
                  <h3 className="fw-bold text-dark mb-1">{Math.max(0, statistics.totalEnrolled - statistics.present)}</h3>
                  <p className="text-muted mb-0 small">{t("absent")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm h-100 bg-brand">
                <div className="card-body text-center text-white">
                  <div className="mb-2">
                    <i className="bi bi-graph-up-arrow fs-1"></i>
                  </div>
                  <h3 className="fw-bold mb-1">{statistics.attendanceRate.toFixed(1)}%</h3>
                  <p className="mb-0 small opacity-75">{t("attendanceRate")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Session Tracking Statistics Row - Only show when end session exists */}
          {hasEndSession && (
            <div className="row mb-4 g-3">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 border-start border-success border-4">
                  <div className="card-body d-flex align-items-center">
                    <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                      <i className="bi bi-check-all text-success fs-4"></i>
                    </div>
                    <div>
                      <h4 className="fw-bold text-dark mb-0">{statistics.fullAttendance || 0}</h4>
                      <p className="text-muted mb-0 small">{t("fullAttendance")}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 border-start border-warning border-4">
                  <div className="card-body d-flex align-items-center">
                    <div className="rounded-circle bg-warning bg-opacity-10 p-3 me-3">
                      <i className="bi bi-box-arrow-right text-warning fs-4"></i>
                    </div>
                    <div>
                      <h4 className="fw-bold text-dark mb-0">{statistics.leftEarly || 0}</h4>
                      <p className="text-muted mb-0 small">{t("leftEarly")}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 border-start border-info border-4">
                  <div className="card-body d-flex align-items-center">
                    <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                      <i className="bi bi-clock-fill text-info fs-4"></i>
                    </div>
                    <div>
                      <h4 className="fw-bold text-dark mb-0">{statistics.late || 0}</h4>
                      <p className="text-muted mb-0 small">{t("late")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Group Statistics */}
      {uniqueGroups.length > 0 && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-0 py-3">
            <h5 className="mb-0 fw-bold">
              <i className="bi bi-diagram-3-fill text-brand me-2"></i>
              {t("groupStatistics")}
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {Object.entries(groupStats).map(([groupName, stats]) => {
                const rate = stats.total > 0 ? (stats.present / stats.total * 100).toFixed(1) : 0;
                return (
                  <div key={groupName} className="col-md-4">
                    <div className="card border shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h6 className="mb-0 fw-bold">
                            <i className="bi bi-people-fill text-secondary me-1"></i>
                            {groupName}
                          </h6>
                          <span className={`badge ${rate >= 70 ? 'bg-success' : rate >= 50 ? 'bg-warning' : 'bg-danger'}`}>
                            {rate}%
                          </span>
                        </div>
                        <div className="d-flex justify-content-between text-sm">
                          <span className="text-muted">{t("present")}: <strong>{stats.present}</strong></span>
                          <span className="text-muted">{t("total")}: <strong>{stats.total}</strong></span>
                        </div>
                        <div className="progress mt-2" style={{ height: '6px' }}>
                          <div
                            className={`progress-bar ${rate >= 70 ? 'bg-success' : rate >= 50 ? 'bg-warning' : 'bg-danger'}`}
                            role="progressbar"
                            style={{ width: `${rate}%` }}
                            aria-valuenow={rate}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-0 py-3">
          <h5 className="mb-0 fw-bold">
            <i className="bi bi-funnel-fill text-brand me-2"></i>
            {t("filterOptions")}
          </h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label small text-muted fw-semibold">{t("filterByStatus")}</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t("allStatuses")}</option>
                {attendanceStatuses.map(status => (
                  <option key={status.id} value={status.id}>
                    {getLocalizedStatusName(status)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small text-muted fw-semibold">{t("filterByGroup")}</label>
              <select
                className="form-select"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
              >
                <option value="all">{t("allGroups")}</option>
                {uniqueGroups.map(group => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small text-muted fw-semibold">&nbsp;</label>
              <button
                className="btn btn-brand w-100"
                onClick={() => {
                  setStatusFilter('all');
                  setGroupFilter('all');
                }}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                {t("resetFilters")}
              </button>
            </div>
          </div>
          <div className="mt-3 text-center">
            <span className="badge bg-light text-dark border px-3 py-2">
              <i className="bi bi-info-circle me-1"></i>
              {t("showing")} <strong>{filteredData.length}</strong> {t("of")} <strong>{attendanceData.length}</strong> {t("records")}
            </span>
          </div>
        </div>
      </div>

      {/* Data Table */}
      {filteredData.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-inbox display-1 text-muted mb-3"></i>
            <h5 className="text-muted">{t("noAttendanceRecords")}</h5>
            <p className="text-muted small">{t("tryAdjustingFilters")}</p>
          </div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            <DataTablePagination
              rows={filteredData}
              columns={columns}
              rowCount={filteredData.length}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              localeTextsMap={localizedTextsMap}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAttendanceList;
